import React, { useCallback, useEffect, useState } from 'react';
import { ACCEPT, deleteFile, fileUrl, formatSize, listFiles, uploadFile, waitIndexed } from '../api/files';
import FileTile from './FileTile';
import '../styles/memory.css';
import '../styles/files.css';

/**
 * Sidebar button + panel: the user's own files. Private to their workspace; searched with the manuals on every
 * question and cited like manual pages. Upload (drop or browse), see each file's status, open it, delete it.
 */
const STATUS = {
  processing: { label: 'Reading…', tone: 'busy' },
  ready: { label: 'Searchable', tone: 'ok' },
  stored: { label: 'Saved, not searchable', tone: 'muted' },
  failed: { label: 'Failed', tone: 'bad' },
};

function Panel({ onClose, t }) {
  const [state, setState] = useState({ loading: true, error: null, files: [], maxMb: 25 });
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState([]);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(() => listFiles()
    .then((d) => setState({ loading: false, error: null, files: d.files || [], maxMb: d.max_mb || 25 }))
    .catch((e) => setState((s) => ({ ...s, loading: false, error: e.message }))), []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  // keep polling while something is still being read
  useEffect(() => {
    if (!state.files.some((f) => f.status === 'processing')) return undefined;
    const id = setTimeout(load, 1500);
    return () => clearTimeout(id);
  }, [state.files, load]);

  const upload = async (list) => {
    const files = Array.from(list || []);
    setBusy((b) => [...b, ...files.map((f) => f.name)]);
    const ids = [];
    for (const f of files) {
      try {
        const row = await uploadFile(f);
        ids.push(row.id);
      } catch (e) {
        setState((s) => ({ ...s, error: `${f.name}: ${(e.response && e.response.data && e.response.data.detail) || e.message}` }));
      }
      setBusy((b) => b.filter((n) => n !== f.name));
      load();
    }
    if (ids.length) waitIndexed(ids, { onUpdate: (fl) => setState((s) => ({ ...s, files: fl })) }).catch(() => {});
  };

  const remove = async (f) => {
    setConfirm(null);
    try {
      await deleteFile(f.id);
    } catch (e) {
      setState((s) => ({ ...s, error: e.message }));
    }
    load();
  };

  const ready = state.files.filter((f) => f.status === 'ready').length;

  return (
    <div className="mg-overlay" role="dialog" aria-modal="true" aria-label={t('My files')} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="mg-panel mf-panel">
        <div className="mg-header">
          <div className="mg-title mf-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" /></svg>
            <span>{t('My files')}</span>
          </div>
          <span className="mg-stats">{state.files.length} {t('files')} · {ready} {t('searchable')}</span>
          <div className="mg-header__actions">
            <button type="button" className="cv-icon-btn" aria-label={t('Close')} title={t('Close')} onClick={onClose}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12" /></svg>
            </button>
          </div>
        </div>
        <p className="mg-sub">
          {t('Only you can see these files. When you ask a question, the assistant searches them together with the FUSO manuals and cites them like manual pages.')}
        </p>
        <div className="mf-body">
          <label
            className={`mf-drop${drag ? ' is-drag' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files); }}
          >
            <input type="file" multiple accept={ACCEPT} hidden onChange={(e) => { upload(e.target.files); e.target.value = ''; }} />
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="M17 8l-5-5-5 5" /><path d="M12 3v12" /></svg>
            <span className="mf-drop__main">{busy.length ? `${t('Uploading')} ${busy.join(', ')}…` : t('Drop files here or click to browse')}</span>
            <span className="mf-drop__sub">PDF, Word, TXT, Markdown, CSV, JSON · {t('images are saved but not searchable')} · {t('max')} {state.maxMb} MB</span>
          </label>
          {state.error && <div className="mf-error">{state.error}</div>}
          {state.loading && <div className="mf-empty"><span className="cv-spinner" /> {t('Loading…')}</div>}
          {!state.loading && !state.files.length && <div className="mf-empty">{t('No files yet.')}</div>}
          <ul className="mf-list">
            {state.files.map((f) => {
              const st = STATUS[f.status] || { label: f.status, tone: 'muted' };
              return (
                <li key={f.id} className="mf-row">
                  <FileTile name={f.filename} className="mf-row__tile" />
                  <div className="mf-row__main">
                    <a className="mf-row__name" href={fileUrl(f.id)} target="_blank" rel="noopener noreferrer" title={t('Open')}>{f.filename}</a>
                    <div className="mf-row__meta">
                      {formatSize(f.size_bytes)}
                      {f.pages ? ` · ${f.pages} ${t('pages')}` : ''}
                      {f.chunks ? ` · ${f.chunks} ${t('passages')}` : ''}
                      {` · ${new Date(f.created_at).toLocaleString()}`}
                    </div>
                    {f.error && <div className="mf-row__err">{f.error}</div>}
                  </div>
                  <span className={`mf-status is-${st.tone}`}>{f.status === 'processing' && <span className="cv-spinner" />}{t(st.label)}</span>
                  <div className="mf-row__actions">
                    <a className="cv-btn cv-btn--small" href={fileUrl(f.id)} target="_blank" rel="noopener noreferrer">{t('Open')}</a>
                    {confirm === f.id ? (
                      <>
                        <button type="button" className="cv-btn cv-btn--small mf-danger" onClick={() => remove(f)}>{t('Delete')}</button>
                        <button type="button" className="cv-btn cv-btn--small" onClick={() => setConfirm(null)}>{t('Cancel')}</button>
                      </>
                    ) : (
                      <button type="button" className="cv-btn cv-btn--small" onClick={() => setConfirm(f.id)}>{t('Delete')}</button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default function MyFilesButton({ translate = (s) => s }) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  return (
    <>
      <button type="button" className="start-new-btn mf-open-btn" onClick={() => setOpen(true)}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" /></svg>
        <span className="btn-text">{translate('My files')}</span>
      </button>
      {open && <Panel onClose={close} t={translate} />}
    </>
  );
}
